import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { CheckCircle2, MessageSquare, Sparkles, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { liveChatApi } from '../../../api/liveChat.api';

const StatCard = ({ icon: Icon, label, value, color }) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      borderRadius: 3,
      border: '1px solid',
      borderColor: 'rgba(15, 23, 42, 0.08)',
      bgcolor: '#ffffff',
      boxShadow: '0 12px 24px rgba(15, 23, 42, 0.06)',
      height: '100%',
    }}
  >
    <Stack direction="row" spacing={2} alignItems="center">
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2,
          bgcolor: `${color}1a`,
          color: color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon size={22} />
      </Box>
      <Box>
        <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: -0.2 }}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

const AgentDashboardPage = ({ session = {} }) => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const agentId = session?.uid || session?.u_id || session?.uId || '';

  const getSessionAgentId = (sessionItem) =>
    sessionItem?.agent_id ||
    sessionItem?.assigned_agent_id ||
    sessionItem?.assigned_to ||
    sessionItem?.agent?.uid ||
    sessionItem?.assigned_agent?.uid ||
    sessionItem?.agent_uid ||
    '';

  const matchesAgent = (sessionItem) => {
    if (!agentId) return true;
    const assignedId = getSessionAgentId(sessionItem);
    if (!assignedId) return true;
    return String(assignedId) === String(agentId);
  };

  useEffect(() => {
    const loadSessions = async () => {
      setLoading(true);
      setErrorMsg('');
      try {
        const response = await liveChatApi.getSessions();
        const payload = response?.data?.data ?? response?.data ?? {};
        const list = Array.isArray(payload.sessions) ? payload.sessions : [];
        setSessions(list);
      } catch (error) {
        setErrorMsg('Failed to load assigned chats');
      } finally {
        setLoading(false);
      }
    };

    loadSessions();
  }, []);

  const assignedSessions = useMemo(
    () => sessions.filter((item) => matchesAgent(item)),
    [sessions, agentId]
  );

  const openSessions = assignedSessions.filter((item) => item.status === 'open');
  const closedSessions = assignedSessions.filter((item) => item.status === 'closed');
  const uniqueCustomers = new Set(assignedSessions.map((item) => item.cd_id)).size;

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        maxWidth: 1300,
        mx: 'auto',
        bgcolor: '#f8fafc',
        borderRadius: { xs: 0, md: 4 },
        minHeight: 'calc(100vh - 120px)',
      }}
    >
      <Card
        elevation={0}
        sx={{
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'rgba(15, 23, 42, 0.08)',
          p: { xs: 3, md: 4 },
          mb: 4,
          background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 55%, #4ade80 100%)',
          color: '#f8fafc',
          boxShadow: '0 18px 36px rgba(15, 23, 42, 0.18)',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} justifyContent="space-between">
          <Box>
            <Chip
              label="Agent Workspace"
              icon={<Sparkles size={14} />}
              sx={{
                bgcolor: 'rgba(255,255,255,0.22)',
                color: '#fff',
                mb: 2,
                fontWeight: 600,
              }}
            />
            <Typography variant="h4" fontWeight={700} gutterBottom>
              {greeting()}, {session?.u_name || session?.name || 'Agent'}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Your assigned conversations and performance overview for today.
            </Typography>
          </Box>
          <Stack spacing={1} alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {dayjs().format('dddd, MMM D')}
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/agent/chat')}
              sx={{
                bgcolor: '#0f172a',
                px: 3,
                py: 1.1,
                borderRadius: 2.5,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: '0 12px 22px rgba(15, 23, 42, 0.25)',
                '&:hover': {
                  bgcolor: '#111827',
                },
              }}
            >
              Go to Live Chat
            </Button>
          </Stack>
        </Stack>
      </Card>

      {errorMsg && (
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'error.light', color: 'error.main' }}>
          {errorMsg}
        </Paper>
      )}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <StatCard icon={MessageSquare} label="Assigned Chats" value={loading ? '-' : assignedSessions.length} color="#2563eb" />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatCard icon={CheckCircle2} label="Open Chats" value={loading ? '-' : openSessions.length} color="#10b981" />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatCard icon={Users} label="Active Customers" value={loading ? '-' : uniqueCustomers} color="#f97316" />
        </Grid>
      </Grid>

      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'rgba(15, 23, 42, 0.08)',
          bgcolor: '#fff',
          boxShadow: '0 16px 32px rgba(15, 23, 42, 0.06)',
        }}
      >
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Assigned Conversations
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Track the latest chats that require your response.
              </Typography>
            </Box>
            <Chip
              label={loading ? 'Loading' : `${openSessions.length} open`}
              color="success"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          </Stack>
          <Stack spacing={2.5}>
            {assignedSessions.slice(0, 6).map((item) => (
              <Paper
                key={item.lcs_id}
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'rgba(15, 23, 42, 0.08)',
                  bgcolor: '#ffffff',
                  boxShadow: '0 10px 20px rgba(15, 23, 42, 0.04)',
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography fontWeight={600}>
                      Session #{item.lcs_id}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.cd_name || item.customer_name || 'Customer'} - {item.channel || 'whatsapp'}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={item.status || 'open'}
                    color={item.status === 'closed' ? 'default' : 'success'}
                  />
                </Stack>
              </Paper>
            ))}
            {!loading && assignedSessions.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No assigned chats yet. Check back soon.
                </Typography>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AgentDashboardPage;

